// Reglas de reservas compartidas por la vista admin, el TPV y la web
// pública. Funciones puras (testeables) — sin React ni axios.

import { apiErrorText, hasApiErrorCode } from "src/utils/apiErrorCode";

export const BOOKINGS_ADDON_NAME = "Reservas online";

// true solo cuando sabemos con certeza que el add-on NO está activo
// (lista de add-ons ya cargada y sin "Reservas online"). Mientras no se
// haya cargado devolvemos false para no ocultar nada antes de tiempo;
// si el backend responde 402 el error se trata en silencio.
export function isBookingsAddOnInactive(state) {
  const addOns = state?.addOns;
  if (!addOns?.activeAddOnsLoaded) return false;
  return !(addOns.activeAddOnNames || []).includes(BOOKINGS_ADDON_NAME);
}

// Acciones que dan lectura de reservas en el backend (BookingController:
// BOOKING_VIEW o cualquiera de las de gestión).
export const BOOKING_READ_ACTIONS = Object.freeze([
  "BOOKING_VIEW", "BOOKING_CREATE", "BOOKING_MODIFY", "BOOKING_CANCEL", "BOOKING_SETTINGS",
]);

// true cuando el empleado fichado NO puede leer reservas. Las cargas de
// fondo (alertas, próximas) no deben pedirlas: el 403 abría el diálogo
// "¿Qué encargado autoriza?" al entrar en cualquier pantalla. Mismo
// criterio que useCanDo: sin sesión de empleado (dueño) o sin rol → puede.
export function cannotReadBookings(state) {
  const info = state?.employeeSession?.info;
  if (!info || !info.roleId) return false;
  const actions = info.actions || [];
  return !BOOKING_READ_ACTIONS.some((a) => actions.includes(a));
}

// BookingSettings.ZoneSelectionMode: 0 Oculto, 1 Opcional, 2 Requerido.
export const ZONE_MODE = Object.freeze({ HIDDEN: 0, OPTIONAL: 1, REQUIRED: 2 });

// Cómo se comporta el paso "Zona" del wizard público.
//   "skip"     → no hay paso de zona (modo Oculto o sin zonas reservables)
//   "optional" → el cliente puede elegir preferencia o "Cualquiera"
//   "required" → debe elegir una zona para continuar
export function zoneStepMode(zoneSelectionMode, zonesCount) {
  if (!zonesCount) return "skip";
  const mode = Number(zoneSelectionMode);
  if (mode === ZONE_MODE.HIDDEN) return "skip";
  if (mode === ZONE_MODE.REQUIRED) return "required";
  // Sin dato (backend antiguo) se mantiene el comportamiento previo.
  return "optional";
}

export function canContinueZoneStep(mode, zoneId) {
  if (mode !== "required") return true;
  return zoneId !== "" && zoneId != null;
}

// Grupo grande: más personas que el umbral → no se reserva online.
export function isLargeGroup(pax, threshold) {
  const t = Number(threshold);
  if (!t || t <= 0) return false;
  return Number(pax) > t;
}

// Plazo de cancelación desde la web. `bookingDate` en hora local del
// restaurante (ISO sin zona, como lo devuelve la API).
export function cancelDeadlineInfo(bookingDate, cancelDeadlineHours, now = new Date()) {
  const hours = Number(cancelDeadlineHours) || 0;
  const start = bookingDate ? new Date(bookingDate) : null;
  if (!start || Number.isNaN(start.getTime())) {
    return { deadline: null, canCancel: true, hours };
  }
  const deadline = new Date(start.getTime() - hours * 3600 * 1000);
  return { deadline, canCancel: now.getTime() <= deadline.getTime(), hours };
}

const BOOKING_ERRORS = [
  ["RATE_LIMITED", "Demasiados intentos, espera un momento y vuelve a probar."],
  ["SLOT_TAKEN", "Esa hora se acaba de ocupar. Hemos actualizado los horarios: elige otra."],
  ["LARGE_GROUP", "Para grupos grandes, llama al restaurante para reservar."],
  ["CANCEL_DEADLINE_PASSED", "Ya no se puede cancelar online: ha pasado el plazo. Llama al restaurante."],
  ["BOOKING_NOT_PENDING", "Esta reserva ya no está pendiente de confirmar."],
  ["TABLE_CONFLICT", "Esa mesa ya tiene otra reserva a esa hora. Elige otra mesa u hora."],
  // El API mantiene el código histórico para el conflicto de mesa.
  ["ErrorBookingOurSameTable", "Esa mesa ya tiene otra reserva a esa hora. Elige otra mesa u hora."],
  ["BOOKING_BUSY", "Hay muchas reservas entrando a la vez. Inténtalo de nuevo en unos segundos."],
  ["ZONE_REQUIRED", "Elige una zona para continuar."],
  ["BOOKINGS_DISABLED", "Este restaurante no acepta reservas online ahora mismo."],
  ["ADDON_REQUIRED", "Las reservas requieren el add-on «Reservas online»."],
];

// Código conocido del error (o null).
export function bookingErrorCode(err) {
  const hit = BOOKING_ERRORS.find(([code]) => hasApiErrorCode(err, code));
  return hit ? hit[0] : null;
}

// Mensaje en castellano para mostrar al usuario.
export function bookingErrorMessage(err, fallback = "No pudimos completar la operación.") {
  const hit = BOOKING_ERRORS.find(([code]) => hasApiErrorCode(err, code));
  if (hit) return hit[1];
  const text = apiErrorText(err);
  // Mensajes legibles del backend (cortos, sin pinta de código técnico).
  if (text && text.length < 200 && /\s/.test(text)) return text;
  return fallback;
}

// ── Comensales en los formularios de reserva ────────────────────────────
// El campo se edita como texto: puede quedar vacío mientras se escribe
// (antes se forzaba a 1 en cada tecla y no se podía borrar para escribir
// otro número). Se valida al salir del campo y al guardar.
export const GUESTS_MIN = 1;
export const GUESTS_MAX = 50;

/** ¿Se acepta lo tecleado? Vacío o hasta 3 dígitos. */
export function acceptGuestsInput(value) {
  const v = value == null ? "" : String(value);
  return v === "" || /^\d{1,3}$/.test(v);
}

/** Número válido de comensales o null si está vacío / no es un número. */
export function parseGuests(value) {
  const v = value == null ? "" : String(value).trim();
  if (v === "" || !/^\d+$/.test(v)) return null;
  return Number(v);
}

/** Al salir del campo: se ajusta al rango; vacío se queda vacío (y se avisa). */
export function normalizeGuestsOnBlur(value) {
  const n = parseGuests(value);
  if (n == null) return "";
  return String(Math.min(GUESTS_MAX, Math.max(GUESTS_MIN, n)));
}

/** Mensaje de error del campo o null si es válido. */
export function guestsError(value) {
  const n = parseGuests(value);
  if (n == null) return "Indica el número de comensales.";
  if (n < GUESTS_MIN) return `Mínimo ${GUESTS_MIN} comensal.`;
  if (n > GUESTS_MAX) return `Máximo ${GUESTS_MAX} comensales.`;
  return null;
}
